/**
 * Le relevé d'une partie en cours.
 *
 * Tout le calcul vit dans `statistiques.ts`, côté `shared`, où il est pur et testé
 * sur de vraies parties. Il ne reste ici que le branchement React — et une seule
 * précaution, la même que pour les annonces.
 *
 * ⚠️ La comparaison se fait sur la **révision**, jamais sur l'identité de l'objet :
 * en ligne, l'état est reconstruit à chaque patch, donc toujours « nouveau ».
 * Comparer les références compterait le même 421 plusieurs fois par seconde.
 */

import { useEffect, useRef, useState } from "react";

import {
  statistiquesVides, suivre, type Partie, type Statistiques,
} from "@zinc421/shared";

/**
 * Suit une partie et accumule ce qu'elle raconte, du point de vue de `monSiege`.
 *
 * Sans siège — hot-seat, où l'appareil sert plusieurs joueurs — il n'y a personne
 * dont tenir les statistiques : on rend un relevé vide plutôt que celui du siège 0,
 * qui serait celui d'un joueur parmi d'autres.
 */
export function useStatistiques(partie: Partie, monSiege?: number): Statistiques {
  const [stats, setStats] = useState<Statistiques>(statistiquesVides);
  const precedent = useRef<Partie | null>(null);
  const derniereRevision = useRef<number>(-1);

  useEffect(() => {
    if (monSiege === undefined) return;
    if (partie.revision === derniereRevision.current) return;
    const avant = precedent.current;
    derniereRevision.current = partie.revision;
    precedent.current = partie;
    if (!avant) return;   // le premier état ne raconte rien : il n'y a pas d'avant

    setStats((courantes) => suivre(courantes, avant, partie, monSiege));
  }, [partie, monSiege]);

  return stats;
}
